import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

interface Recipient {
  id: string;
  name: string;
  email_address: string;
  status: string;
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounce_reason: string;
  has_merge_fields?: boolean;
  has_password?: boolean;
}

interface CampaignData {
  id: string;
  name: string;
  subject: string;
  sender_type: string;
  recipient_group: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  total_recipients: number;
  sent_count: number;
  queued_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  open_rate: number;
  click_rate: number;
  recipients: Recipient[];
}

interface CampaignDetailProps {
  campaignId: string;
  onBack: () => void;
  onEdit: (id: string) => void;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: '#f0f0f0', color: '#666' },
  queued: { bg: '#f0f0f0', color: '#666' },
  sent: { bg: '#e8f5e9', color: '#2e7d32' },
  opened: { bg: '#e3f2fd', color: '#1565c0' },
  clicked: { bg: '#f3e5f5', color: '#7b1fa2' },
  bounced: { bg: '#fce4ec', color: '#c62828' },
  failed: { bg: '#fff3e0', color: '#e65100' },
};

const SENDER_LABELS: Record<string, string> = {
  brad: 'Brad Murphy',
  noreply: 'No-Reply',
  office: 'Office',
};

const GROUP_LABELS: Record<string, string> = {
  all: 'All Customers',
  wholesale: 'Wholesale',
  retail_partners: 'Retail Partners',
  bridge_club: 'Bridge Club',
  farmhand: 'Farmhand Users',
  custom: 'Custom',
};

const CampaignDetail: React.FC<CampaignDetailProps> = ({ campaignId, onBack, onEdit }) => {
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  // Send Test state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testSearch, setTestSearch] = useState('');
  const [testSending, setTestSending] = useState('');
  const [testSuccess, setTestSuccess] = useState('');

  // Resend state
  const [resetting, setResetting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/campaigns/${campaignId}/`);
      setCampaign(res.data);
    } catch (err) {
      setError('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await api.post(`/admin/campaigns/${campaignId}/duplicate/`);
      onEdit(res.data.id);
    } catch (err) {
      setError('Failed to duplicate campaign');
    } finally {
      setDuplicating(false);
    }
  };

  const handleSendTest = async (recipientId: string, recipientName: string) => {
    setTestSending(recipientId);
    setTestSuccess('');
    try {
      const res = await api.post(`/admin/campaigns/${campaignId}/send-test/`, {
        recipient_id: recipientId,
      });
      setTestSuccess(`Test sent to ${res.data.sent_to} (${recipientName})`);
    } catch (err: any) {
      setTestSuccess(`Failed: ${err.response?.data?.error || 'Unknown error'}`);
    } finally {
      setTestSending('');
    }
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      const res = await api.post(`/admin/campaigns/${campaignId}/resume/`);
      if (res.data.queued_count === 0) {
        alert(res.data.message);
      }
      await loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resume campaign');
    } finally {
      setResuming(false);
    }
  };

  const handleCancel = async (forceComplete: boolean) => {
    const msg = forceComplete
      ? 'Force-complete this campaign? It will be marked as "sent" with whatever was already delivered. Remaining queued recipients will NOT be sent.'
      : 'Cancel this campaign? The background send will stop.';
    if (!window.confirm(msg)) return;
    setCancelling(true);
    try {
      await api.post(`/admin/campaigns/${campaignId}/cancel/`, { force_complete: forceComplete });
      await loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel campaign');
    } finally {
      setCancelling(false);
    }
  };

  const handleResetForResend = async () => {
    if (!window.confirm(
      'Reset this campaign for resend? All recipient statuses will be reset to queued. Merge fields (email, password) will be preserved.'
    )) return;

    setResetting(true);
    try {
      await api.post(`/admin/campaigns/${campaignId}/reset-for-resend/`);
      await loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset campaign');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>Loading campaign...</p>;
  }

  if (error || !campaign) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: '#8B0000' }}>{error || 'Campaign not found'}</p>
        <button onClick={onBack} style={{ marginTop: '1rem', padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
          Back to Campaigns
        </button>
      </div>
    );
  }

  const filteredRecipients = statusFilter
    ? campaign.recipients.filter(r => r.status === statusFilter)
    : campaign.recipients;

  const canEdit = campaign.status !== 'sending';
  const isSent = campaign.status === 'sent';
  const isSending = campaign.status === 'sending';
  const hasQueued = (campaign.queued_count || 0) > 0;

  // Filter recipients for test modal search
  const testFilteredRecipients = testSearch.trim()
    ? campaign.recipients.filter(r =>
        r.name.toLowerCase().includes(testSearch.toLowerCase()) ||
        r.email_address.toLowerCase().includes(testSearch.toLowerCase())
      )
    : campaign.recipients;

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#2D5A27', cursor: 'pointer', padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.9rem' }}>
        &larr; Back to Campaigns
      </button>

      {/* Campaign Info Header */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 0.35rem 0', fontSize: '1.25rem' }}>{campaign.name}</h2>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.95rem' }}>Subject: {campaign.subject}</p>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#999' }}>
              <span>From: {SENDER_LABELS[campaign.sender_type] || campaign.sender_type}</span>
              <span>Audience: {GROUP_LABELS[campaign.recipient_group] || campaign.recipient_group}</span>
              {campaign.sent_at && <span>Sent: {new Date(campaign.sent_at).toLocaleString()}</span>}
              {campaign.scheduled_at && campaign.status === 'scheduled' && (
                <span>Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {canEdit && (
              <button
                onClick={() => onEdit(campaign.id)}
                style={{ padding: '0.5rem 1rem', background: '#2D5A27', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Edit
              </button>
            )}
            {campaign.recipients.length > 0 && (
              <button
                onClick={() => { setShowTestModal(true); setTestSuccess(''); setTestSearch(''); }}
                style={{ padding: '0.5rem 1rem', background: '#1565c0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Send Test
              </button>
            )}
            {(isSending || (isSent && hasQueued)) && (
              <button
                onClick={handleResume}
                disabled={resuming}
                style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: resuming ? 0.6 : 1 }}
              >
                {resuming ? 'Resuming...' : 'Resume Send'}
              </button>
            )}
            {isSending && (
              <>
                <button
                  onClick={() => handleCancel(true)}
                  disabled={cancelling}
                  style={{ padding: '0.5rem 1rem', background: '#e65100', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: cancelling ? 0.6 : 1 }}
                >
                  {cancelling ? 'Stopping...' : 'Force Complete'}
                </button>
                <button
                  onClick={() => handleCancel(false)}
                  disabled={cancelling}
                  style={{ padding: '0.5rem 1rem', background: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: cancelling ? 0.6 : 1 }}
                >
                  Cancel Send
                </button>
              </>
            )}
            {isSent && (
              <button
                onClick={handleResetForResend}
                disabled={resetting}
                style={{ padding: '0.5rem 1rem', background: '#e65100', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: resetting ? 0.6 : 1 }}
              >
                {resetting ? 'Resetting...' : 'Resend'}
              </button>
            )}
            <button
              onClick={handleDuplicate}
              disabled={duplicating}
              style={{ padding: '0.5rem 1rem', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {duplicating ? 'Duplicating...' : 'Duplicate'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <StatCard label="Total Sent" value={campaign.sent_count} />
        {hasQueued && <StatCard label="Queued" value={campaign.queued_count} color="#e65100" />}
        <StatCard label="Opened" value={campaign.opened_count} suffix={campaign.total_recipients > 0 ? ` (${campaign.open_rate}%)` : ''} color="#1565c0" />
        <StatCard label="Clicked" value={campaign.clicked_count} suffix={campaign.total_recipients > 0 ? ` (${campaign.click_rate}%)` : ''} color="#7b1fa2" />
        <StatCard label="Bounced" value={campaign.bounced_count} color={campaign.bounced_count > 0 ? '#c62828' : undefined} />
        {(campaign.recipients?.filter(r => r.status === 'failed').length || 0) > 0 && (
          <StatCard label="Failed" value={campaign.recipients.filter(r => r.status === 'failed').length} color="#e65100" />
        )}
      </div>

      {/* Failed Emails Alert */}
      {(() => {
        const failedRecipients = campaign.recipients?.filter(r => r.status === 'failed') || [];
        if (failedRecipients.length === 0) return null;
        return (
          <div style={{
            background: '#fff8f0', border: '1px solid #f97316', borderRadius: '8px',
            padding: '1rem 1.25rem', marginBottom: '1.25rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.1rem' }}>&#9888;</span>
              <strong style={{ color: '#c2410c', fontSize: '0.95rem' }}>
                {failedRecipients.length} email{failedRecipients.length !== 1 ? 's' : ''} could not be delivered
              </strong>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #fed7aa', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.5rem', color: '#9a3412', fontWeight: 600 }}>Customer</th>
                  <th style={{ padding: '0.4rem 0.5rem', color: '#9a3412', fontWeight: 600 }}>Email</th>
                  <th style={{ padding: '0.4rem 0.5rem', color: '#9a3412', fontWeight: 600 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {failedRecipients.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #fef3c7' }}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{r.name}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#c62828', fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.email_address || '(empty)'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#9a3412' }}>{r.bounce_reason || 'Send failed'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#9a3412' }}>
              These customers were not reached. Consider contacting them by phone or other means.
            </p>
          </div>
        );
      })()}

      {/* Recipients Table */}
      {campaign.recipients && campaign.recipients.length > 0 && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>
              Recipients ({filteredRecipients.length}{statusFilter ? ` ${statusFilter}` : ''})
            </h3>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              {['', 'sent', 'opened', 'clicked', 'bounced', 'failed', 'queued'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: statusFilter === s ? '#2D5A27' : 'white',
                    color: statusFilter === s ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Sent</th>
                <th style={thStyle}>Opened</th>
                <th style={thStyle}>Clicked</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipients.map(r => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>
                      {r.name}
                      {r.has_merge_fields && (
                        <span style={{ marginLeft: '6px', fontSize: '0.65rem', padding: '1px 5px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '8px' }}>
                          merge
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.85rem', color: '#666' }}>{r.email_address}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        backgroundColor: sc.bg,
                        color: sc.color,
                      }}>
                        {r.status}
                      </span>
                      {r.bounce_reason && (
                        <div style={{ fontSize: '0.7rem', color: '#c62828', marginTop: '2px' }}>{r.bounce_reason}</div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#666' }}>
                      {r.sent_at ? new Date(r.sent_at).toLocaleString() : '-'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#666' }}>
                      {r.opened_at ? new Date(r.opened_at).toLocaleString() : '-'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#666' }}>
                      {r.clicked_at ? new Date(r.clicked_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {campaign.recipients && campaign.recipients.length === 0 && campaign.status === 'draft' && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999', background: 'white', borderRadius: '8px' }}>
          <p>No recipients yet. Edit the campaign to select recipients and send.</p>
        </div>
      )}

      {/* Send Test Modal */}
      {showTestModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '1.5rem', width: '500px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Send Test Email</h3>
              <button onClick={() => setShowTestModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#999' }}>
                &times;
              </button>
            </div>

            <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#666' }}>
              Send a real campaign email (with [TEST] prefix) to a recipient. Merge fields like email and password will be filled in.
            </p>

            {testSuccess && (
              <div style={{
                padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.85rem',
                background: testSuccess.startsWith('Failed') ? '#fce4ec' : '#e8f5e9',
                color: testSuccess.startsWith('Failed') ? '#c62828' : '#2e7d32',
              }}>
                {testSuccess}
              </div>
            )}

            <input
              type="text"
              placeholder="Search recipients by name or email..."
              value={testSearch}
              onChange={e => setTestSearch(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem', marginBottom: '0.75rem' }}
            />

            <div style={{ overflowY: 'auto', flex: 1, maxHeight: '400px' }}>
              {testFilteredRecipients.slice(0, 50).map(r => (
                <div key={r.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0.5rem', borderBottom: '1px solid #f0f0f0',
                }}>
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {r.name}
                      {r.has_merge_fields && (
                        <span style={{ marginLeft: '6px', fontSize: '0.6rem', padding: '1px 5px', background: '#e3f2fd', color: '#1565c0', borderRadius: '8px' }}>
                          has merge data
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{r.email_address}</div>
                  </div>
                  <button
                    onClick={() => handleSendTest(r.id, r.name)}
                    disabled={testSending === r.id}
                    style={{
                      padding: '0.3rem 0.75rem', background: '#1565c0', color: 'white',
                      border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                      opacity: testSending === r.id ? 0.6 : 1,
                    }}
                  >
                    {testSending === r.id ? 'Sending...' : 'Send'}
                  </button>
                </div>
              ))}
              {testFilteredRecipients.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>No recipients match</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; suffix?: string; color?: string }> = ({ label, value, suffix, color }) => (
  <div style={{ background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
    <div style={{ fontSize: '1.5rem', fontWeight: 600, color: color || '#333' }}>
      {value.toLocaleString()}{suffix || ''}
    </div>
    <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>{label}</div>
  </div>
);

const thStyle: React.CSSProperties = {
  padding: '0.75rem 0.5rem',
  fontSize: '0.8rem',
  color: '#666',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.5rem',
  fontSize: '0.9rem',
};

export default CampaignDetail;
